// Models
import { Shipping, ShippingHelper } from './shipping';
import { Product, ProductHelper } from './product';
import { ShippingOption } from './shippingOption';
import * as _ from 'lodash';

/**
 * Interface to map items with id
 *
 * @interface CartItemexport
 */
interface CartItems {
    [index: string]: CartItem;
}

interface CartOption {
    value?: string;
    label: string;
}

export interface CartItemAdd {
    product: Product;
    quantity: number;
}

export enum CartItemStatus {
    Validated,
    Validating,
}

export interface CartItem {
    product_id: string;
    product: Product;
    quantity: number;
    price: number;
    priceO: number;
    precioConDescuento: number;
    precioSinDescuento: number;
    totalLinea: number;
    totalLineaSinDescuento: number;
    shippings: Shipping[];
    options: CartOption[];
    status: CartItemStatus;
    parts: boolean;
    json: string;
    portesTipoEntrega: number;
}

export class CartItemHelper {
    static create(product_id: string, product: Product, quantity: number, status: CartItemStatus): CartItem {
        const productCopy = _.cloneDeep(product);

        const cartItem = {
            product_id: product_id,
            product: productCopy,
            quantity: quantity,
            price: productCopy.rates.base.precioMostrarBusqueda,
            priceO: productCopy.rates.base.precioMostrarPedido,
            totalLinea: 0,
            totalLineaSinDescuento: 0,
            precioConDescuento: 0,
            precioSinDescuento: 0,
            shippings: [],
            options: [],
            status: CartItemStatus.Validating,
            parts: false,
            json: '',
            portesTipoEntrega: 0,
        };

        return CartItemHelper.updateQuantityOptions(cartItem);
    }

    static createFromPart(part: any): CartItem {
        const cartItem: CartItem = {
            product_id: part['ARTICULO'],
            product: ProductHelper.create(),
            quantity: part['CANTIDAD'],
            price: part['PRECIO'],
            priceO: part['PRECIO'],
            totalLinea: part['TOTAL'],
            totalLineaSinDescuento: part['TOTAL'],
            precioConDescuento: part['PRECIO'],
            precioSinDescuento: part['PRECIOSINDESCUENTO'],
            shippings: [],
            options: CartItemHelper.buildQuantityOptions(1, part['CANT_MAX'], 1),
            status: CartItemStatus.Validated,
            parts: true,
            json: part['JSON_EXTERNO'],
            portesTipoEntrega: 0,
        };

        cartItem.product.rates.base.preDto1 = part['DTO1'];
        cartItem.product.rates.base.preDto2 = part['DTO2'];
        cartItem.product.rates.base.preDto3 = part['DTO3'];
        cartItem.product.rates.base.preDto4 = part['DTO4'];
        cartItem.product.rates.base.preDto5 = part['DTO5'];

        cartItem.product.info.nombre = part['DESCRIPCION'];

        cartItem.product.rates.base.prePrm = 0;

        // Appends sends
        const shipping = new Shipping();
        shipping.agencia = part['AGENCIA'].trim();
        shipping.almacen = part['ALMACEN'];
        shipping.almacenCodigo = part['ALMACEN_CODIGO'];
        shipping.tiempo = part['TIEMPO'];
        shipping.tiempo2 = part['TIEMPO2'];
        shipping.cantidad = part['CANTIDAD'];
        shipping.descripcion = part['DESCRIPCION'];
        shipping.colorEntrega = part['COLORENTREGA'] ? part['COLORENTREGA'] : 0;

        // Shipping creation
        if (part['ENTREGAS_DISPONIBLES']) {
            // let apiShippingOptions = JSON.parse(row["ENTREGAS_DISPONIBLES"]);
            try {
                const apiShippingOptions = JSON.parse(part['ENTREGAS_DISPONIBLES']);
                if ('entregas' in apiShippingOptions) {
                    for (const apiShipping of apiShippingOptions.entregas) {
                        const shippingOption = new ShippingOption();
                        shippingOption.id = apiShipping.id;
                        shippingOption.tipoEntrega = apiShipping.tipoEntrega;
                        shippingOption.descripcion = apiShipping.descripcion;
                        shippingOption.orden = apiShipping.orden;
                        shippingOption.seleccionado = apiShipping.seleccionado === '1';
                        shippingOption.descuento = apiShipping.descuento ? apiShipping.descuento : 0;
                        shippingOption.fechaEntrega = apiShipping.fechaEntrega;
                        shipping.shippingOptions.push(shippingOption);
                    }
                }
            } catch (msg) {
                console.log('[cart][validateItems] Error formato json entregas');
            }
        }

        cartItem.shippings.push(shipping);

        return cartItem;
    }

    static increment(cartItem: CartItem, quantity: number): CartItem {
        cartItem.quantity += quantity;
        return cartItem;
    }

    static setQuantity(cartItem: CartItem, quantity: number): CartItem {
        cartItem.quantity = quantity;
        if (cartItem.parts) {
            cartItem.shippings[0].cantidad = quantity;
        }
        return cartItem;
    }

    static setPrice(cartItem: CartItem, price: number): CartItem {
        return {
            ...cartItem,
            price: price,
            priceO: price,
            product: {
                ...cartItem.product,
                rates: {
                    ...cartItem.product.rates,
                    base: { ...cartItem.product.rates.base, precioMostrarBusqueda: price },
                },
            },
            precioSinDescuento: price,
        };
    }

    static setDiscount1(cartItem: CartItem, discount: number): CartItem {
        // console.log('setDiscount', cartItem, discount);
        return {
            ...cartItem,
            product: {
                ...cartItem.product,
                rates: {
                    ...cartItem.product.rates,
                    base: { ...cartItem.product.rates.base, preDto1: discount },
                },
            },
        };
    }

    static setDiscount2(cartItem: CartItem, discount: number): CartItem {
        // console.log('setDiscount', cartItem, discount);
        return {
            ...cartItem,
            product: {
                ...cartItem.product,
                rates: { ...cartItem.product.rates, base: { ...cartItem.product.rates.base, preDto2: discount } },
            },
        };
    }

    static buildQuantityOptions(min: any, max: any, increment: any): CartOption[] {
        // console.log('buildQuantityOptions', {min},{max},{increment});

        const quantityOptions: CartOption[] = [];

        // Fill the array
        let pos = min;

        while (pos < max + 1) {
            quantityOptions.push({ value: pos, label: pos });
            pos += pos === min && increment > min ? increment - min : increment;
        }

        return quantityOptions;
    }

    static updateQuantityOptions(cartItem: CartItem): CartItem {
        cartItem.options = [];
        cartItem.options = CartItemHelper.buildQuantityOptions(
            cartItem.product.cantMin,
            cartItem.product.cantMax,
            cartItem.product.cantMult,
        );

        return cartItem;
    }

    static totalMostrar(cartItem: CartItem): number {
        const totalShow = cartItem.totalLinea; // cartItem.quantity * cartItem.product.rates.base.precioMostrarBusqueda;
        return totalShow;
    }

    /**
     * Get total of item
     *
     * @readonly
     * @type {number}
     * @memberOf CartItem
     */
    static total(cartItem: CartItem, recalculate: boolean = true): number {
        // console.log('Total - cartItem', cartItem);
        // let total: number;

        // const preDto1 = Number(cartItem.product.rates.base.preDto1);
        // const preDto2 = Number(cartItem.product.rates.base.preDto2);
        // const preTotal = cartItem.priceO * cartItem.quantity;

        // // Both Discounts
        // if (preDto1 && preDto2) {
        //     total = cartItem.priceO
        //         * cartItem.quantity
        //         * ((100 - preDto1) / 100);

        //     total = total * ((100 - preDto2) / 100);

        // } else if (preDto1 || preDto2) {
        //     // Discount 1
        //     if (preDto1) {
        //         total = cartItem.priceO * cartItem.quantity * ((100 - preDto1) / 100);
        //     }

        //     // Discount 2
        //     if (preDto2) {
        //         total = cartItem.priceO * cartItem.quantity * ((100 - preDto2) / 100);
        //     }
        // } else {
        //     // No discount
        //     total = cartItem.priceO * cartItem.quantity;
        // }

        // console.log(recalculate, preTotal, cartItem.totalLinea, cartItem.totalLineaSinDescuento);

        //cartItem.totalLinea = (!recalculate) ? total : preTotal;

        return cartItem.totalLinea;
    }

    static mergeShippings(cartItem: CartItem, shippings: Shipping[]): CartItem {
        // Map older shippings
        const mapshipping = {};
        for (const shipping of cartItem.shippings) {
            const hash = ShippingHelper.getHash(shipping);
            mapshipping['h_' + hash] = shipping;
        }
        // New shipping
        const new_shipping = [];
        for (const shipping of shippings) {
            if (mapshipping['h_' + ShippingHelper.getHash(shipping)]) {
                shipping.shippingOptionSelected =
                    mapshipping['h_' + ShippingHelper.getHash(shipping)].shippingOptionSelected;
            }
            new_shipping.push(shipping);
        }
        cartItem.shippings = new_shipping;

        return cartItem;
    }
}
